import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  anlegen as sendeAnlage,
  liste as holeListe,
  positionen as holePositionen,
  type BenutzerZeile,
  type Position,
} from '../api/verwaltung';
import { ApiFehler } from '../api/fehler';
import { Liste } from '../komponenten/Liste';
import { Meldung } from '../komponenten/Meldung';
import { Segmente } from '../komponenten/Segmente';
import { useKopfhandlungen, useZaehler, zaehlwort } from '../komponenten/Seitenkontext';

type Statuswahl = '' | BenutzerZeile['status'];

const STATUSWAHL: readonly { readonly wert: Statuswahl; readonly text: string }[] = [
  { wert: '', text: 'Alle' },
  { wert: 'aktiv', text: 'Aktiv' },
  { wert: 'gesperrt', text: 'Gesperrt' },
  { wert: 'deaktiviert', text: 'Deaktiviert' },
];

/**
 * Die Benutzerliste (BEN-01, BEN-11, BEN-13).
 *
 * Sie zeigt drei Dinge, die man auf einen Blick braucht und die sonst je
 * Konto einzeln nachzusehen wären:
 *
 *   DIE ROLLEN. Ohne sie ist eine Benutzerliste eine Namensliste.
 *   DEN ZWEITEN FAKTOR. Ein Konto ohne ihn kommt nicht herein, solange die
 *     Richtlinie ihn verlangt — das sieht man dem Status nicht an.
 *   DIE LETZTE ANMELDUNG. Ein Konto, das seit Monaten niemand benutzt, ist
 *     der Kandidat für BEN-13.
 *
 * SEITENTYP LISTE (ÄA-41): Zähler und „Konto anlegen …" im Seitenkopf,
 * darunter Suche und Status als Segmente, die Rastertabelle in einer Karte.
 * Das neue Konto wird in der Seitenleiste erfasst — die Liste bleibt dabei
 * sichtbar, und wer einen Anmeldenamen vergibt, sieht die vorhandenen.
 */
export function Benutzerliste({
  darfPflegen,
  beiBenutzer,
}: {
  readonly darfPflegen: boolean;
  readonly beiBenutzer: (id: string) => void;
}): JSX.Element {
  const [liste, setzeListe] = useState<readonly BenutzerZeile[]>([]);
  const [positionen, setzePositionen] = useState<readonly Position[]>([]);
  const [suche, setzeSuche] = useState('');
  const [status, setzeStatus] = useState<Statuswahl>('');
  const [anlage, setzeAnlage] = useState(false);
  const [kennwort, setzeKennwort] = useState<{ name: string; wert: string } | undefined>(undefined);
  const [laeuft, setzeLaeuft] = useState(false);
  const [laedt, setzeLaedt] = useState(true);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);

  const [neu, setzeNeu] = useState({
    anmeldename: '',
    name: '',
    email: '',
    kontoart: 'person' as 'person' | 'technisch',
    position: '',
  });

  const laden = useCallback(async (): Promise<void> => {
    setzeListe(
      await holeListe({
        ...(suche === '' ? {} : { suche }),
        ...(status === '' ? {} : { status }),
      }),
    );
  }, [suche, status]);

  useEffect(() => {
    void laden()
      .catch((ursache: unknown) => setzeFehler(ursache as ApiFehler))
      .finally(() => setzeLaedt(false));
  }, [laden]);

  useEffect(() => {
    void holePositionen()
      .then(setzePositionen)
      .catch(() => setzePositionen([]));
  }, []);

  /* ÄA-41: der Zähler im Seitenkopf — erst, wenn geladen ist. */
  useZaehler(laedt ? undefined : zaehlwort(liste.length, 'Konto', 'Konten'));

  /* BEN-01: die Haupthandlung im Seitenkopf, nur mit dem Recht zu pflegen. */
  useKopfhandlungen(
    darfPflegen && !anlage ? (
      <button type="button" className="knopf knopf--haupt" onClick={() => setzeAnlage(true)}>
        Konto anlegen …
      </button>
    ) : undefined,
    [darfPflegen, anlage],
  );

  const zeit = (wert: string | null): string =>
    wert === null ? 'nie' : new Date(wert).toLocaleDateString('de-DE');

  async function handle(was: () => Promise<void>): Promise<void> {
    setzeLaeuft(true);
    try {
      await was();
      setzeFehler(undefined);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaeuft(false);
    }
  }

  const anlageformular = (
    <>
      <div className="karte__titel">
        <h2>Neues Konto</h2>
        {/* BEN-01, BEN-13 */}
      </div>

      <div className="feld">
        <label className="feld__beschriftung" htmlFor="bn-anmeldename">
          Anmeldename
        </label>
        <input
          id="bn-anmeldename"
          type="text"
          value={neu.anmeldename}
          placeholder="vorname.nachname"
          onChange={(e) => setzeNeu({ ...neu, anmeldename: e.target.value })}
        />
        <p className="feld__hilfe">
          Buchstaben, Ziffern, Punkt, Bindestrich und Unterstrich. Er lässt sich später
          nicht ändern — er steht in jedem Protokolleintrag.
        </p>
      </div>

      <div className="feld">
        <label className="feld__beschriftung" htmlFor="bn-name">
          Name
        </label>
        <input
          id="bn-name"
          type="text"
          value={neu.name}
          onChange={(e) => setzeNeu({ ...neu, name: e.target.value })}
        />
      </div>

      <div className="feld">
        <label className="feld__beschriftung" htmlFor="bn-email">
          E-Mail
        </label>
        <input
          id="bn-email"
          type="email"
          value={neu.email}
          onChange={(e) => setzeNeu({ ...neu, email: e.target.value })}
        />
      </div>

      <div className="feld">
        <label className="feld__beschriftung" htmlFor="bn-position">
          Position
        </label>
        <select
          id="bn-position"
          value={neu.position}
          onChange={(e) => setzeNeu({ ...neu, position: e.target.value })}
        >
          <option value="">— ohne Vorlage —</option>
          {positionen.map((p) => (
            <option key={p.kennung} value={p.kennung}>
              {p.bezeichnung}
            </option>
          ))}
        </select>
        {/* BEN-13 */}
        {/* BEN-09 */}
        <p className="feld__hilfe">
          Die Position bringt ihre Rollen mit. <strong>Keine</strong> Vorlage
          enthält die Administratorrolle — die wird einzeln vergeben und braucht die
          Bestätigung einer zweiten Person.
        </p>
      </div>

      <div className="feld">
        <label className="feld__beschriftung" htmlFor="bn-kontoart">
          Kontoart
        </label>
        <select
          id="bn-kontoart"
          value={neu.kontoart}
          onChange={(e) =>
            setzeNeu({ ...neu, kontoart: e.target.value as 'person' | 'technisch' })
          }
        >
          <option value="person">Person</option>
          <option value="technisch">technisch (Schnittstelle)</option>
        </select>
        {/* BEN-11 */}
        <p className="feld__hilfe">
          Ein technisches Konto meldet sich nicht an der Oberfläche an und bekommt kein
          Kennwort.
        </p>
      </div>

      <div className="knopfreihe">
        <button type="button" className="knopf" onClick={() => setzeAnlage(false)}>
          Abbrechen
        </button>
        <button
          type="button"
          className="knopf knopf--haupt"
          disabled={laeuft}
          onClick={() =>
            void handle(async () => {
              const ergebnis = await sendeAnlage({
                anmeldename: neu.anmeldename.trim(),
                name: neu.name.trim(),
                email: neu.email.trim(),
                kontoart: neu.kontoart,
                team: null,
                sichtbarkeit: 'alle',
                position: neu.position === '' ? null : neu.position,
              });
              if (ergebnis.anfangskennwort !== null) {
                setzeKennwort({ name: neu.name, wert: ergebnis.anfangskennwort });
              }
              setzeAnlage(false);
              setzeNeu({
                anmeldename: '', name: '', email: '', kontoart: 'person', position: '',
              });
              await laden();
            })
          }
        >
          Anlegen
        </button>
      </div>
    </>
  );

  return (
    <>
      {fehler !== undefined && <Meldung fehler={fehler} />}

      {/*
        BEN-01: Das Anfangskennwort steht EINMAL da und nirgends sonst — es
        wird nicht gespeichert, nur sein Hash. Deshalb bleibt es stehen, bis
        es jemand wegklickt, statt nach ein paar Sekunden zu verschwinden.
      */}
      {kennwort !== undefined && (
        <div className="hinweiskarte">
          <p>
            Anfangskennwort für <strong>{kennwort.name}</strong>:{' '}
            <code className="geheimnis__wert">{kennwort.wert}</code>
          </p>
          {/* BEN-06 */}
          <p className="feld__hilfe">
            Es steht <strong>nur hier</strong> — gespeichert wird allein sein Streuwert. Geben
            Sie es weiter und schließen Sie diesen Hinweis; danach ist es nicht mehr
            abrufbar. Es gilt bis zur ersten Anmeldung, dann vergibt der Benutzer selbst
            eines.
          </p>
          <button
            type="button"
            className="knopf knopf--klein"
            onClick={() => setzeKennwort(undefined)}
          >
            Habe ich notiert
          </button>
        </div>
      )}

      <Liste
        suche={{
          wert: suche,
          aendern: setzeSuche,
          beschriftung: 'Benutzer suchen',
          platzhalter: 'Name, Anmeldename oder E-Mail',
        }}
        filter={
          <Segmente
            beschriftung="Status"
            optionen={STATUSWAHL}
            gewaehlt={status}
            beiWahl={setzeStatus}
          />
        }
        {...(darfPflegen && anlage
          ? { vorschau: anlageformular, vorschauBeschriftung: 'Neues Konto erfassen' }
          : {})}
      >
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle">
            <caption className="nur-vorlesen">{liste.length} Konten</caption>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Rollen</th>
                <th scope="col">Status</th>
                <th scope="col">Zweiter Faktor</th>
                <th scope="col">Letzte Anmeldung</th>
              </tr>
            </thead>
            <tbody>
              {liste.map((b) => (
                <tr key={b.id}>
                  <td>
                    <button type="button" className="liste__oeffnen" onClick={() => beiBenutzer(b.id)}>
                      {b.name}
                    </button>
                    <span className="kanalliste__name"> · {b.anmeldename}</span>
                  </td>
                  <td>
                    {b.rollen.length === 0 ? (
                      /*
                        Ein Konto ohne Rolle kann sich anmelden und sieht
                        nichts. Das ist kein Fehler, aber fast immer ein
                        Versehen — und als leere Zelle fällt es nicht auf.
                      */
                      <span className="marke marke--inaktiv">keine Rolle</span>
                    ) : (
                      b.rollenBezeichnungen.join(', ')
                    )}
                  </td>
                  <td>
                    <span className={b.status === 'aktiv' ? 'marke' : 'marke marke--inaktiv'}>
                      {b.status}
                    </span>
                    {b.kontoart === 'technisch' && (
                      <span className="kanalliste__name"> · technisch</span>
                    )}
                    {/*
                      RES-28: Das Dienstleisterkonto traegt seine Ressource. Ohne
                      sie sieht man der Zeile nicht an, wessen Zugang man sperrt —
                      und gesperrt wird hier, eingerichtet wird an der Ressource.
                    */}
                    {b.kontoart === 'trainer' && (
                      <span className="kanalliste__name">
                        {' '}
                        · Onlinezugang{b.ressourceName === null ? '' : `: ${b.ressourceName}`}
                      </span>
                    )}
                  </td>
                  <td>
                    {b.kontoart === 'technisch' ? (
                      // BEN-11: Ein technisches Konto meldet sich nicht an der
                      // Oberfläche an; ein zweiter Faktor wäre dort sinnlos.
                      <span className="kanalliste__name">entfällt</span>
                    ) : !b.kennwortGesetzt ? (
                      /*
                        OHNE KENNWORT KOMMT NIEMAND HEREIN — und dann ist der
                        zweite Faktor die falsche Auskunft.
  
                        Das Konto existiert, trägt seine Rolle und seine Ressource
                        und sieht in jeder anderen Spalte fertig aus. Genau so
                        stehen seit dem 12.09.2026 zweiundvierzig Trainerzugänge
                        da: angelegt, damit die Postfächer eingerichtet werden
                        können, und noch ohne Weg hinein. Der Weg ist
                        „Kennwort zurücksetzen" in der Akte (BEN-21) oder der
                        Zugangslink nach RES-28.
                      */
                      <span className="marke marke--warn">kein Kennwort</span>
                    ) : b.zweiterFaktorEingerichtet ? (
                      <span className="marke">eingerichtet</span>
                    ) : (
                      <span className="marke marke--inaktiv">fehlt</span>
                    )}
                  </td>
                  <td className="liste__einzeilig">{zeit(b.letzteAnmeldung)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!laedt && liste.length === 0 && (
          <div className="liste__leer" role="status">
            <p>
              {suche === '' && status === ''
                ? 'Noch kein Konto.'
                : `Kein Treffer${suche === '' ? '' : ` für „${suche}“`}.`}
            </p>
          </div>
        )}
      </Liste>
    </>
  );
}
